import { Box } from '@mui/material';
import { z } from 'zod';

import Heading from '@/features/Dashboard/components/Heading';
import { SEASONAL_CHART_MAX_ITEMS } from '@/features/Dashboard/constants/const';
import { DrawerStep } from '@/features/Dashboard/drawer/types';
import { ColorPalette } from '@/features/Dashboard/form/AppForm/components/ColorPaletteSelector/const';
import { ItemSelection } from '@/features/Dashboard/form/AppForm/components/ItemSelection';
import { SelectChartType } from '@/features/Dashboard/form/AppForm/components/SelectChartType';
import { SelectSeasonalRange } from '@/features/Dashboard/form/AppForm/components/SelectSeasonalRange';
import { TabName } from '@/features/Dashboard/form/AppForm/components/TabName';
import type { ChartFormValues } from '@/features/Dashboard/form/AppForm/context/types';
import type { SeasonalChartWidgetV2 } from '@/features/Dashboard/types';
import { AppType } from '@/features/Dashboard/types';

import type { FormAppDefinition } from '../types';
import {
  buildChart,
  ColorPaletteSchema,
  graphToFormValues,
  NameSchema,
  SymbolsStateSchema
} from './_helpers';

export const seasonalChartDefinition: FormAppDefinition<ChartFormValues, SeasonalChartWidgetV2> = {
  type: AppType.Seasonal,
  drawerStep: DrawerStep.SelectSpotChart,
  dashboardControls: { edit: true },
  widgetControls: { export: true },
  maxItems: SEASONAL_CHART_MAX_ITEMS,
  initialValues: {
    name: 'Spot History chart',
    type: AppType.Seasonal,
    symbols: { active: [], backlog: [] },
    colorPalette: ColorPalette.Vibrant
  },
  schema: z.object({
    id: z.string().optional(),
    type: z.literal(AppType.Seasonal),
    name: NameSchema,
    symbols: SymbolsStateSchema,
    colorPalette: ColorPaletteSchema,
    from: z.string().optional()
  }),
  Form: () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Heading
        subtitle='Set up your chart preferences and data'
        title='Configure spot chart'
      />
      <TabName />
      <SelectChartType />
      <SelectSeasonalRange />
      <ItemSelection />
    </Box>
  ),
  toWidget: values => buildChart(values),
  canEditWidget: (w): w is SeasonalChartWidgetV2 =>
    w.type === 'graph' && w.metadata?.graphType === 'seasonal',
  fromWidget: w => graphToFormValues(w)
};
